/**
 * WHAT THE PRINTER SAID, NOT WHAT A PARSER DECIDED IT MEANT.
 *
 * The old parsers (`parseStatus1..5`) collapsed each reply byte to ONE code and threw the byte
 * away, so a reply nobody had ever seen — and no IP1000 reply has ever been recorded — was reported
 * as a confident sentence. Worse, the vendor sources disagree about where "black mark not found"
 * lives (the SDK says DLE EOT 3 bit 0x20, Masung's command-set PDF says bit 0x04, the Windows DLL
 * reads DLE EOT 2 bit 0x40), and the old code picked one.
 *
 * This keeps the raw byte, names EVERY set bit with its source, and reports disagreement as
 * disagreement. A setup screen showing `0x12 → online` is evidence; "Printer is ready" is a claim.
 */

export type Severity = 'ok' | 'info' | 'warn' | 'error';

export interface StatusFlag {
  /** Short operator-facing phrase. */
  label: string;
  severity: Severity;
  /** What to physically do about it, if anything. */
  fix?: string;
}

export interface StatusReading {
  query: 1 | 2 | 3 | 4 | 5;
  /** `null` = the printer did not answer within the timeout. That is a finding, not a zero. */
  raw: number | null;
  flags: StatusFlag[];
}

const hex = (b: number) => '0x' + b.toString(16).padStart(2, '0');
export const rawHex = (b: number | null) => (b === null ? 'no reply' : hex(b));

export function decodeStatus(query: 1 | 2 | 3 | 4 | 5, raw: number | null, opts: { markMode?: boolean } = {}): StatusReading {
  const flags: StatusFlag[] = [];
  if (raw === null) {
    flags.push({
      label: 'No reply',
      severity: query === 5 ? 'info' : 'warn',
      fix: query === 5
        ? 'Normal for printers without a presenter.'
        : 'The printer accepted the command but sent nothing back. Printing can still work; status checks will not.',
    });
    return { query, raw, flags };
  }
  const b = raw;
  switch (query) {
    case 1:
      // Masung fixes bits 1, 2 and 4 high (0x16). Not the Epson meaning of those bits.
      // ✅ FIELD (EP802 APP22-11-11-A5): healthy 0x16; in error 0x1E — bit 0x08 is OFFLINE, and an
      // offline printer silently discards print data. The first version called 0x1E "Online".
      if (b & 0x08) flags.push({ label: 'OFFLINE — the printer is ignoring print data', severity: 'error', fix: 'See the cause below. Open and close the lid, or press FEED once, then check again.' });
      else if ((b & 0x16) === 0x16) flags.push({ label: 'Online', severity: 'ok' });
      else flags.push({ label: `Unexpected pattern (${hex(b)})`, severity: 'warn', fix: 'Not a Masung status signature. It may be a different command set or a different make.' });
      break;
    case 2:
      if (b & 0x04) flags.push({ label: 'Cover / head open', severity: 'error', fix: 'Close the printer lid until it clicks.' });
      if (b & 0x20) flags.push({ label: 'Paper out (offline)', severity: 'error', fix: 'Load the paper roll.' });
      if (b & 0x40) flags.push({
        label: 'Offline because of an error (cause below). The Windows SDK reads this as "black mark not found"',
        severity: 'error',
      });
      break;
    case 3:
      if (b & 0x08) flags.push({ label: 'Cutter not home / jammed', severity: 'error', fix: 'Open the lid, clear any paper at the blade, close it.' });
      if (b & 0x40) flags.push({ label: 'Head temperature abnormal', severity: 'warn', fix: 'Let it cool for a minute; it recovers by itself.' });
      if (b & 0x20) flags.push({ label: 'Black mark not found (per Masung SDK)', severity: opts.markMode ? 'error' : 'info', fix: 'Marks on the BACK of the stock, sensor on the mark side, paper type set to black mark.' });
      if (b & 0x04) flags.push({ label: 'Mechanical error, or BLACK MARK NOT FOUND (Masung command set)', severity: 'error', fix: 'With mark detection on, the printer searched for a mark and gave up. Check marks face the sensor, or set paper type to plain in step 7.' });
      break;
    case 4:
      if ((b & 0x60) === 0x60) flags.push({ label: 'Paper out', severity: 'error', fix: 'Load the paper roll.' });
      else if (b & 0x60) flags.push({ label: 'Paper sensor partly reports out', severity: 'warn' });
      if ((b & 0x0C) === 0x0C) flags.push({ label: 'Paper near end', severity: 'warn', fix: 'Have a new roll ready.' });
      // ✅ FIELD (EP802 APP22-11-11-A5): 0x12 right after power-on or a settings restart, 0x13 once
      // anything has printed — and it does NOT clear when the ticket is taken. A fact about the
      // printer's session, not about any one ticket.
      flags.push(b & 0x01
        ? { label: 'Has printed since it was switched on or restarted', severity: 'info' }
        : { label: 'Nothing printed since it was switched on or restarted', severity: 'info' });
      break;
    case 5:
      // Kiosk extension. Base value 0x10 per the command set.
      if (b & 0x80) flags.push({ label: 'Presenter error', severity: 'error' });
      if (b & 0x01) flags.push({ label: 'Paper blocked at the exit', severity: 'error', fix: 'Clear the paper exit / bezel.' });
      if (b & 0x08) flags.push({ label: 'Paper jam', severity: 'error', fix: 'Open the lid and clear the jam.' });
      if (b & 0x02) flags.push({ label: 'Ticket was pulled', severity: 'info' });
      if (b & 0x04) flags.push({ label: 'Ticket waiting at exit (not taken)', severity: 'info' });
      break;
  }
  if (!flags.some((f) => f.severity !== 'ok' && f.severity !== 'info') && !flags.some((f) => f.severity === 'ok')) {
    flags.unshift({ label: query === 4 ? 'Paper present' : 'Nothing reported', severity: 'ok' });
  }
  return { query, raw, flags };
}

export const worstSeverity = (rs: StatusReading[]): Severity => {
  const order: Severity[] = ['ok', 'info', 'warn', 'error'];
  return rs.flatMap((r) => r.flags.map((f) => f.severity)).reduce<Severity>((w, s) => (order.indexOf(s) > order.indexOf(w) ? s : w), 'ok');
};

export const STATUS_QUERY_NAMES: Record<number, string> = {
  1: 'Printer',
  2: 'Offline cause',
  3: 'Error cause',
  4: 'Paper sensor',
  5: 'Kiosk / presenter',
};
